import { FC, useMemo, useCallback, ReactNode, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PagesPaths } from '@/enums';
import { User } from '@/types/auth';
import {
  clearUserData,
  getAccessToken,
  setUserData,
  isAuthenticated,
} from '@/utils/auth';
import { decodeJWT } from '@/utils/decodeJWT';
import { useInactivityTimer } from '@/hooks/useInactivityTimer';

import { AuthContext, NotificationType } from './AuthContext';

interface LocationState {
  from: {
    pathname: string;
  };
}
type AuthProviderProps = {
  children: ReactNode;
};

export type UserDataDecodedProps = {
  username: string;
  name: string;
  email: string;
  phone: string;
  armadores: string;
  facwebcc: string;
  cliente_hub_frio: string;
  estado_cuenta_titular: string;
  agente_retencion: string;
  cambioseta: NotificationType;
  armadoresDashboard: string;
  ftpVERMAS?: string;
  ultimo_rut_cliente_liquidacion_electronica_vacio_import?: string;
};

export const AuthProvider: FC<AuthProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const accessToken = getAccessToken();
  const decodedJWTTokenData = accessToken ? decodeJWT(accessToken) : null;
  const [userDataDecoded, setUserDataDecoded] = useState<UserDataDecodedProps>(
    accessToken
      ? decodeJWT(accessToken)
      : {
          username: decodedJWTTokenData?.username ?? '',
          name: decodedJWTTokenData?.name ?? '',
          email: decodedJWTTokenData?.email ?? '',
          phone: decodedJWTTokenData?.phone ?? '',
          armadores: decodedJWTTokenData?.armadores ?? '',
          facwebcc: decodedJWTTokenData?.facwebcc ?? '',
          cliente_hub_frio: decodedJWTTokenData?.cliente_hub_frio ?? '',
          estado_cuenta_titular:
            decodedJWTTokenData?.estado_cuenta_titular ?? '',
          agente_retencion: decodedJWTTokenData?.agente_retencion ?? '',
          cambioseta: decodedJWTTokenData?.cambioseta ?? 'Ninguna',
          armadoresDashboard: decodedJWTTokenData?.armadoresDashboard ?? '',
          ftpVERMAS: decodedJWTTokenData?.ftpVERMAS ?? '',
          ultimo_rut_cliente_liquidacion_electronica_vacio_import:
            decodedJWTTokenData?.ultimo_rut_cliente_liquidacion_electronica_vacio_import ??
            '',
        }
  );

  const handleLogin = useCallback(
    (userData: User) => {
      setUserData(userData);
      if (userData.accessToken) {
        const decodedData = decodeJWT(userData.accessToken);

        if (decodedData) {
          setUserDataDecoded({
            username: decodedData.username || '',
            name: decodedData.name || '',
            email: decodedData.email || '',
            phone: decodedData.phone || '',
            armadores: decodedData.armadores || '',
            facwebcc: decodedData.facwebcc || '',
            cliente_hub_frio: decodedData.cliente_hub_frio || '',
            estado_cuenta_titular: decodedData.estado_cuenta_titular || '',
            agente_retencion: decodedData.agente_retencion || '',
            cambioseta: decodedData.cambioseta || 'Ninguna',
            armadoresDashboard: decodedData.armadoresDashboard || '',
            ftpVERMAS: decodedData.ftpVERMAS || '',
          });
        }
      }
      const origin =
        (location.state as LocationState)?.from?.pathname ||
        PagesPaths.PrivateHome;
      navigate(origin, { replace: true });
    },
    [location.state, navigate]
  );

  const handleLogout = useCallback(() => {
    clearUserData();
    navigate(PagesPaths.Login);
    sessionStorage.removeItem('valorNotificacion');
  }, [navigate]);

  // Automatic logout after 20 minutes of inactivity
  const handleInactivityTimeout = useCallback(() => {
    // eslint-disable-next-line no-console
    console.log('User inactive for 20 minutes, logging out automatically');
    handleLogout();
  }, [handleLogout]);

  // Set up inactivity timer (20 minutes = 1200000 milliseconds)
  useInactivityTimer({
    timeout: 20 * 60 * 1000, // 20 minutes
    onTimeout: handleInactivityTimeout,
    enabled: isAuthenticated(), // Only enable when user is logged in
  });

  const value = useMemo(
    () => ({
      onLogin: handleLogin,
      onLogout: handleLogout,
      userDataDecoded,
    }),
    [handleLogin, handleLogout, userDataDecoded]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
